import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from './MainLayout';
import { supabase } from '../lib/supabase_client';
import { useSession } from '../contexts/SessionContext';
import '../styles/page-base.css';

// ─── small reusable bits ────────────────────────────────────────────────────

const Badge: React.FC<{ label: string; color: string }> = ({ label, color }) => {
  const colors: Record<string, string> = {
    green:  'bg-green-100 text-green-700 border border-green-200',
    yellow: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
    red:    'bg-red-100 text-red-700 border border-red-200',
    gray:   'bg-gray-100 text-gray-600 border border-gray-200',
    blue:   'bg-blue-100 text-blue-700 border border-blue-200',
    purple: 'bg-purple-100 text-purple-700 border border-purple-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 rounded hover:bg-gray-700 transition-colors"
      title="Copy"
    >
      {copied ? (
        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'json' }) => (
  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
      <span className="text-xs text-gray-400 font-mono">{language}</span>
      <CopyButton text={code} />
    </div>
    <pre className="px-4 py-3 text-sm text-gray-100 overflow-x-auto font-mono leading-relaxed whitespace-pre">{code}</pre>
  </div>
);

interface ResponseRowProps {
  code: number;
  color: string;
  meaning: string;
  when: string;
}

const ResponseRow: React.FC<ResponseRowProps> = ({ code, color, meaning, when }) => (
  <tr className="border-b border-gray-100 last:border-0">
    <td className="py-2.5 pr-4 w-24">
      <Badge label={String(code)} color={color} />
    </td>
    <td className="py-2.5 pr-4 text-sm font-medium text-gray-800 w-36">{meaning}</td>
    <td className="py-2.5 text-sm text-gray-500">{when}</td>
  </tr>
);

// ─── page ────────────────────────────────────────────────────────────────────

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://<project-ref>.supabase.co';
const ENDPOINT = `${SUPABASE_URL}/functions/v1/api-upload-pdf`;

interface ApiKey {
  id: string;
  key_prefix: string;
  key_suffix: string;
  status: 'active' | 'revoked' | 'expired';
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  deleted_at: string | null;
}

export default function ApiDocsPage() {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { permissions, activeOrg } = useSession();
  const [activeTab, setActiveTab] = useState<'request' | 'response200' | 'response402' | 'response401'>('request');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);

  const fetchApiKeys = useCallback(async () => {
    if (!companyId) {
      setApiKeys([]);
      return;
    }

    try {
      const { data: keysData, error: keysError } = await supabase
        .from('api_keys')
        .select('id, key_prefix, key_suffix, status, created_at, last_used_at, expires_at, deleted_at')
        .eq('org_id', companyId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (keysError) throw keysError;
      setApiKeys(keysData || []);
    } catch (error) {
      console.error('Error fetching API keys for docs:', error);
      setApiKeys([]);
    }
  }, [companyId]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const getFullApiKey = useCallback((keyPrefix: string, keySuffix: string): string => {
    const storedKey = sessionStorage.getItem(`api_key_${companyId}`);

    if (storedKey && storedKey.includes(keySuffix)) {
      return storedKey;
    }

    return `${keyPrefix}_${keySuffix}`;
  }, [companyId]);

  const primaryKey = apiKeys[0] || null;

  const displayedApiKey = useMemo(
    () => primaryKey
      ? getFullApiKey(primaryKey.key_prefix, primaryKey.key_suffix)
      : 'Generate a new API key in the API Keys page first.',
    [primaryKey, getFullApiKey]
  );

  const curlExample = `curl -X POST "${ENDPOINT}" \\
  -H "Authorization: Bearer ${primaryKey ? displayedApiKey : 'YOUR_API_KEY'}" \\
  -F "file=@annual_report.pdf"`;

  const response200 = JSON.stringify({
    success: true,
    filing_id: "d3de88d5-c3a0-4454-8101-ab57d6512f22",
    status: "processing",
    credits_remaining: 4,
    workflow_execution_arn: "arn:aws:states:ap-southeast-1:123456789:execution:..."
  }, null, 2);

  const response402 = JSON.stringify({
    error: "Insufficient credits. Please top up your wallet before uploading.",
    credits_remaining: 0
  }, null, 2);

  const response401 = JSON.stringify({
    error: "Invalid or inactive API key"
  }, null, 2);

  const response400 = JSON.stringify({
    error: "Only PDF files are accepted"
  }, null, 2);

  const tabs = [
    { key: 'request',     label: 'Request' },
    { key: 'response200', label: '200 Success' },
    { key: 'response402', label: '402 No Credits' },
    { key: 'response401', label: '401 Unauthorized' },
  ] as const;

  if (!permissions?.['settings_api_key']) {
    return (
      <MainLayout>
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-yellow-800 mb-2">
              Access Denied
            </h2>
            <p className="text-yellow-700">
              You don't have permission to view API documentation. Only users with integration access can open this page.
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Integration/API docs are not available for Singapore organizations
  if (activeOrg?.countryIso === 'SG') {
    return (
      <MainLayout>
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-yellow-800 mb-2">
              Integration Not Available
            </h2>
            <p className="text-yellow-700">
              API documentation is currently available for Malaysia organizations only.
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">

          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
              <h1 className="page-title">API Documentation</h1>
            </div>
            <div className="page-header-right">
              <button
                type="button"
                onClick={() => navigate(`/integration/${companyId}`)}
                className="f-btn f-btn-success"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Integration</span>
              </button>
            </div>
          </div>

          {/* Endpoint */}
          <div className="page-card">
            {/* Endpoint title bar */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
              <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-bold rounded">POST</span>
              <code className="text-sm font-mono text-gray-700 break-all">/functions/v1/api-upload-pdf</code>
              <Badge label="Credits: 1" color="purple" />
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">
                This endpoint lets you upload a single PDF filing through API. If the upload is accepted, the system creates a filing record, starts processing it, and returns the filing ID together with your remaining credits.
              </p>
              {/* Request & Response tabs */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex bg-gray-50 border-b border-gray-200">
                  {tabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-4 py-2.5 text-xs font-medium transition-colors ${
                        activeTab === tab.key
                          ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-4">
                  {activeTab === 'request' && (
                    <div className="space-y-4">
                      {/* Headers */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Headers</p>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left">
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Key</th>
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Value</th>
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Required</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2.5 font-mono text-sm text-purple-700">Authorization</td>
                              <td className="px-3 py-2.5 text-gray-600 text-sm font-mono break-all">
                                {`Bearer ${primaryKey ? displayedApiKey : 'YOUR_API_KEY'}`}
                              </td>
                              <td className="px-3 py-2.5"><Badge label="Required" color="red" /></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Body */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Body — multipart/form-data</p>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left">
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Field</th>
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Type</th>
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                              <th className="px-3 py-2 text-xs font-semibold text-gray-500">Required</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t border-gray-100">
                              <td className="px-3 py-2.5 font-mono text-sm text-purple-700">file</td>
                              <td className="px-3 py-2.5 text-gray-600 text-sm">File (.pdf)</td>
                              <td className="px-3 py-2.5 text-gray-600 text-sm">The PDF file to process. Only one file per request.</td>
                              <td className="px-3 py-2.5"><Badge label="Required" color="red" /></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* cURL */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">cURL Example</p>
                        <CodeBlock code={curlExample} language="bash" />
                      </div>
                    </div>
                  )}

                  {activeTab === 'response200' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge label="200" color="green" />
                        <span className="text-sm text-gray-600">Upload accepted and processing started. 1 credit deducted.</span>
                      </div>
                      <CodeBlock code={response200} />
                      <table className="w-full text-sm mt-2">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-left">Field</th>
                            <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-left">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['filing_id', 'UUID of the created filing. Use this to track processing status.'],
                            ['status', 'Always "processing" on success.'],
                            ['credits_remaining', 'Your remaining credit balance after deduction.'],
                            ['workflow_execution_arn', 'AWS Step Functions execution ARN for internal tracing.'],
                          ].map(([field, desc]) => (
                            <tr key={field} className="border-t border-gray-100">
                              <td className="px-3 py-2.5 font-mono text-xs text-purple-700">{field}</td>
                              <td className="px-3 py-2.5 text-sm text-gray-600">{desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {activeTab === 'response402' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge label="402" color="yellow" />
                        <span className="text-sm text-gray-600">No credits remaining. Top up before retrying.</span>
                      </div>
                      <CodeBlock code={response402} />
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                        <strong>N8N tip:</strong> Check the HTTP status code. If <code>402</code>, branch your workflow to send a top-up notification before retrying.
                      </div>
                    </div>
                  )}

                  {activeTab === 'response401' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge label="401" color="red" />
                        <span className="text-sm text-gray-600">Missing, invalid, or revoked API key.</span>
                      </div>
                      <CodeBlock code={response401} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Response codes summary */}
          <div className="page-card">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">Response Codes</h2>
            </div>
            <div className="px-6 py-5">
              <table className="w-full">
                <tbody>
                  <ResponseRow code={200} color="green"  meaning="OK"                  when="File uploaded, pipeline triggered, credit deducted." />
                  <ResponseRow code={400} color="gray"   meaning="Bad Request"          when="No file attached, or file is not a PDF." />
                  <ResponseRow code={401} color="red"    meaning="Unauthorized"         when="API key missing, invalid format, revoked, or deleted." />
                  <ResponseRow code={402} color="yellow" meaning="Payment Required"     when="Wallet has 0 credits. Check credits_remaining in response." />
                  <ResponseRow code={500} color="gray"   meaning="Server Error"         when="Internal error. Contact support with your filing_id if returned." />
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="page-card">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">Notes</h2>
            </div>
            <ul className="px-6 py-5 space-y-2.5 text-sm text-gray-600 list-disc list-inside">
              <li>Only <strong>.pdf</strong> files are accepted. One file per request.</li>
              <li>Each successful upload deducts <strong>1 credit</strong> from your wallet.</li>
              <li>Credits are deducted <em>after</em> the file is successfully stored — a failed upload never charges you.</li>
              <li>Filings created via API are labelled <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">API</span> in the filings list.</li>
              <li>Processing typically completes within a few minutes. Poll your filings list or set up a webhook to be notified.</li>
              <li>If the response includes a <code className="bg-gray-100 px-1 rounded">warning</code> field, the file was stored but the processing pipeline could not start — contact support with your <code className="bg-gray-100 px-1 rounded">filing_id</code>.</li>
            </ul>
          </div>

        </div>
      </div>
    </MainLayout>
  );
}
